<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAuctionEvent } from '@/shell/stores/auctionEvent';
// FIXME: cross-feature import — extract to shell bus in Phase 3
import { useMyBidsStore } from '@/features/my-bids';

const route = useRoute();
const router = useRouter();
const auctionEvent = useAuctionEvent();
const myBids = useMyBidsStore();

// Last-resort slug from the URL's first path segment. Covers the brief window
// after a hard load where the route/store haven't resolved yet — without it a
// nav tap in that window builds a slug-less path and is dropped (GT1-430).
function slugFromLocation(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname.split('/').filter(Boolean)[0] ?? '';
}

const slug = computed(() => {
  const routeSlug = typeof route.params.slug === 'string' ? route.params.slug : '';
  return routeSlug || auctionEvent.slug || slugFromLocation();
});

interface Tab {
  key: 'catalog' | 'my-bids' | 'wishlist' | 'wins' | 'account';
  label: string;
  icon: string;
  path: () => string;
  matches: () => boolean;
  badge?: () => 'red' | 'green' | null;
  // Screen-reader text for the badge dot — without it the indicator is
  // color-only and invisible to assistive tech.
  badgeLabel?: string;
}

const tabs: Tab[] = [
  {
    key: 'catalog',
    label: 'Catalog',
    icon: 'pi pi-th-large',
    path: () => `/${slug.value}/items`,
    matches: () => route.path.endsWith('/items') || route.path.includes('/items/'),
  },
  {
    key: 'my-bids',
    label: 'My Bids',
    icon: 'pi pi-bookmark',
    path: () => `/${slug.value}/my-bids`,
    matches: () => route.path.endsWith('/my-bids'),
    badge: () => (myBids.losing.length > 0 ? 'red' : null),
    badgeLabel: "You've been outbid",
  },
  {
    key: 'wishlist',
    label: 'Wishlist',
    icon: 'pi pi-heart',
    path: () => `/${slug.value}/wishlist`,
    matches: () => route.path.endsWith('/wishlist'),
  },
  {
    key: 'wins',
    label: 'Wins',
    icon: 'pi pi-star',
    path: () => `/${slug.value}/wins`,
    matches: () => route.path.endsWith('/wins'),
    badge: () =>
      auctionEvent.event?.status === 'closed' && myBids.won.length > 0
        ? 'green'
        : null,
    badgeLabel: 'You have items to review',
  },
  {
    key: 'account',
    label: 'Account',
    icon: 'pi pi-user',
    path: () => `/${slug.value}/account`,
    matches: () => route.path.includes('/account'),
  },
];

function navigate(tab: Tab): void {
  if (!slug.value) return;
  router.push(tab.path());
}
</script>

<template>
  <nav
    class="bnav"
    aria-label="Primary"
    data-testid="bottom-nav"
  >
    <div class="bnav__inner">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="bnav__tab"
        :class="{ 'bnav__tab--active': tab.matches() }"
        :data-testid="`bottom-nav__${tab.key}`"
        @click="navigate(tab)"
      >
        <span class="bnav__icon-wrap">
          <i
            :class="tab.icon"
            aria-hidden="true"
          />
          <span
            v-if="tab.badge?.()"
            class="bnav__dot"
            :class="`bnav__dot--${tab.badge?.()}`"
            role="img"
            :aria-label="tab.badgeLabel"
            :data-testid="tab.key === 'my-bids' && tab.badge?.() === 'red' ? 'bottom-nav__outbid-indicator' : undefined"
          />
        </span>
        <span class="bnav__label">{{ tab.label }}</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.bnav {
  display: flex;
  justify-content: center;
  background: var(--app-background-elevated);
  border-top: var(--app-border-width-default) solid var(--app-border-muted);
}
.bnav__inner {
  display: flex;
  width: 100%;
  max-width: var(--app-well-max-width);
}
.bnav__tab {
  all: unset;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--app-space-4);
  padding: var(--app-space-8) var(--app-space-4);
  cursor: pointer;
  color: var(--app-text-muted);
  border-top: 2px solid transparent;
  text-align: center;
}
.bnav__tab:focus-visible {
  outline: var(--app-border-width-default) solid var(--app-border-hover);
  outline-offset: -2px;
}
.bnav__tab--active {
  color: var(--app-text-primary);
  border-top-color: var(--app-text-primary);
}
.bnav__icon-wrap {
  position: relative;
  font-size: var(--app-icon-size-lg);
  line-height: 1;
}
.bnav__dot {
  position: absolute;
  top: -2px;
  right: -4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid var(--app-background-elevated);
}
.bnav__dot--red {
  background: var(--app-danger);
}
.bnav__dot--green {
  background: var(--app-success);
}
.bnav__label {
  font-size: var(--app-font-size-caption);
  line-height: var(--app-line-height-caption);
  font-weight: var(--app-font-weight-medium);
}
</style>
